import React from 'react';
import { Tooltip, Box, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import { ConvenienceFeeRestriction } from 'vopay';
import { Title } from 'UI';
import styles from './RestrictionsTooltip.module.scss';

const classes = withStyles(styles);

export interface RestrictionsTooltipProps {
  restrictions: ConvenienceFeeRestriction[];
  country?: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'topLeft' | 'topRight' | 'bottomLeft';
  iconWidth?: string;
  buttonWidth?: string;
}

const RestrictionsTooltip: React.FC<RestrictionsTooltipProps> = ({ restrictions, country, position = 'right' }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'RestrictionsTooltip' });

  // Determine if we should show states or provinces based on country
  const isCanada = country?.toLowerCase() === 'canada' || country?.toLowerCase() === 'ca';
  const regionTypePlural = isCanada ? t('provinces') : t('states');

  const filteredRestrictions = country
    ? restrictions.filter((restriction) => restriction.CountryAbbreviation?.toLowerCase() === country.toLowerCase())
    : restrictions;

  const exemptRegions = filteredRestrictions
    .map((restriction) => ({
      name: restriction.Province,
      abbreviation: restriction.ProvinceAbbreviation,
      country: restriction.Country,
    }))
    .filter(
      (region, index, self) =>
        // Remove duplicates based on abbreviation
        index === self.findIndex((r) => r.abbreviation === region.abbreviation),
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  if (exemptRegions.length === 0) {
    return null;
  }

  const tooltipContent = (
    <Box gapSize="12px" flx="col" className={classes('tooltipContent')}>
      <Title emphasized level="6">
        {t('listOfRegionsWhereFeeWaived', { regionTypePlural })}
      </Title>
      <ul className={classes('restrictionsList')}>
        {exemptRegions.map((region) => (
          <li key={region.abbreviation}>
            {region.name} ({region.abbreviation})
          </li>
        ))}
      </ul>
    </Box>
  );

  return (
    <span
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <Tooltip
        icon="info"
        variant="outlined"
        iconWidth="10px"
        buttonWidth="18px"
        position={position}
        information={tooltipContent}
        hasArrow
        width="340px"
      />
    </span>
  );
};

export default RestrictionsTooltip;
